/**
 * The surface a real `<table>` wears in the redesigned admin, and the card it
 * sits in.
 *
 * **Why these are `sx` helpers and not a component.** The Contests list's table
 * (`pages/contests/contests-table.tsx`) is a CSS grid of divs, because its
 * column widths are one `grid-template-columns` shared by the header and every
 * row — something a real table cannot express. That is the right shape for a
 * designed screen with fixed columns. It is the wrong shape for the eight
 * screens CAE-1556 brought over: their tables have per-screen column counts,
 * colspan-ed states and nested sections, and rebuilding each as a grid would be
 * the bespoke redesign that ticket's scope explicitly rules out. So they stay
 * MUI tables and wear the design through these.
 *
 * **Everything here comes from `theme.tokens`.** Before CAE-1556 the same
 * helpers were `borderRadius: 3` with `boxShadow: 0 1px 4px
 * alpha(theme.palette.common.black, 0.06)` and a head row grounded on
 * `alpha(theme.palette.grey[500], 0.06)` — a 12px radius, a heavier shadow and
 * a grey the design never picked, which is precisely what made a list screen
 * read as a different component from the redesigned one beside it. The
 * `alpha()` derivations were also unreachable from the token layer: moving the
 * §1 ramp would not have moved them. `src/no-hard-coded-colour.test.ts` is what
 * holds that line now.
 */
import type { Theme } from '@mui/material/styles';

/**
 * The redesign's card: 14px radius, the 0.5px hairline and the one shadow §5
 * allows, on `--surface`.
 *
 * The same treatment the Contests list's table frame and its pagination bar
 * wear, said once so a filter bar, a form panel and the table under them
 * cannot drift into three radii.
 */
export const adminCardSx = (theme: Theme) => ({
  backgroundColor: theme.tokens.color.surface,
  border: theme.tokens.border.default,
  borderRadius: `${theme.tokens.radius.card}px`,
  boxShadow: theme.tokens.shadow.card,
});

/**
 * The card for a table whose own corners have to be clipped.
 *
 * `adminCardSx` plus the clip: a table's own corners are square, so without
 * `overflow: hidden` the head row's ground and the last row's hairline paint
 * over the card's rounding.
 *
 * Named for `AdminPagedTable`'s `containerSx` until CAE-1556 deleted that
 * component; the one caller left is the contest entry detail page, whose table
 * has no pagination to put below it either way.
 */
export const adminListTableContainerSx = (theme: Theme) => ({
  ...adminCardSx(theme),
  overflow: 'hidden',
});

/**
 * The card a list sits in when its pagination is the separate bar below rather
 * than a footer welded into the bottom border — which, since CAE-1556, is
 * every screen that pages.
 *
 * The scroll lives here, as it does on the Contests list's frame, so a narrow
 * viewport moves the table inside its own container and never the page body.
 * `overflowX` rather than `overflow`: the vertical clip would cut a row's
 * focus ring.
 *
 * The same card holds the empty state. An empty state is not a table with no
 * rows in it — the column headings describe data that is not there, and the
 * ARIA roles only mean anything with the whole chain present — so the screens
 * put one *in place of* the table, inside this frame.
 */
export const adminListFrameSx = (theme: Theme) => ({
  ...adminCardSx(theme),
  overflowX: 'auto',
});

/** `.cl-thead` — the muted band the column headings sit on. */
export const adminListTableHeadRowSx = (theme: Theme) => ({
  backgroundColor: theme.tokens.color.surfaceMuted,
  '& th': { borderBottom: theme.tokens.border.default },
});

/**
 * `.cl-thead`'s own type: 10.5px/600 micro-caps in `--ink-4`.
 *
 * Not the 0.7rem/700 at 0.08em in `text.secondary` these tables used to carry.
 * Both are "small uppercase grey", which is why the difference survived so
 * long unnoticed — and why it reads as two components once they are on screen
 * together.
 */
export const adminListTableHeadCellSx = (theme: Theme) => ({
  py: 1.25,
  px: 2,
  fontSize: '10.5px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: theme.tokens.color.ink4,
  borderBottom: theme.tokens.border.default,
});

/**
 * A body row: the hover the redesigned rows take, and no hairline under the
 * last one — the card's own edge is already there, and a rule against it reads
 * as a double border.
 */
export const adminListTableRowSx = (theme: Theme) => ({
  transition: 'background 0.12s',
  '&:hover': { backgroundColor: theme.tokens.color.surfaceMuted },
  '&:last-of-type td': { borderBottom: 0 },
});

/**
 * A body cell. The density is the one the screens already used (18px top and
 * bottom); what changes is the rule beneath it, from MUI's 1px `divider` to the
 * design's hairline.
 */
export const adminListTableCellSx = (theme: Theme) => ({
  py: 2.25,
  px: 2,
  borderBottom: theme.tokens.border.default,
});
